import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationPrevious, PaginationNext, PaginationEllipsis } from "@/components/ui/pagination";
import { Calendar } from "@/components/ui/calendar";
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertCircle, Search, ArrowLeft, TrendingUp, Settings, Calendar as CalendarIcon, Undo2, CheckCircle2, XCircle, Clock, Save, GraduationCap, Wrench } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { SidebarProvider } from "@/components/ui/sidebar";
import AdminSidebar from "../Sidebar";
import Header from "../Header";
import { db, auth } from "@/firebase/config"; // Added auth import
import { collection, setDoc, doc, onSnapshot, deleteDoc, getDoc, getDocs, query, where } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";

interface PromoteStudentsFormProps {
  onBack: () => void;
}

interface Student {
  id: string;
  registrationNumber: string;
  name: string;
  currentClass: string;
  currentClassUid: string;
  gradeAverage: number;
  coreSubjectsPassed: boolean;
  teacherApproval: boolean;
  promoted: boolean;
  promotedDate?: Date;
  promotionYear?: string;
}

interface ClassData {
  classUid: string;
  name: string;
  level: number;
  stream: string;
  namingConvention: string;
  stage?: string;
  stageType?: string; // From ClassesManagement
  bridgeToGrade?: string;
  nextClassUid?: string; // Direct pointer to next class
}

interface PromotionHistory {
  id: string;
  studentId: string;
  registrationNumber: string;
  name: string;
  fromClass: string;
  fromClassUid: string;
  toClass: string;
  toClassUid: string;
  promotionYear: string;
  promotedDate: Date;
  isGraduation?: boolean;
  approved?: boolean;
  approvedAt?: string;
  autoApproved?: boolean;
}

interface Affiliation {
  role: string;
  schoolID?: string;
}

const PromoteStudentsForm = ({ onBack }: PromoteStudentsFormProps) => {
  const navigate = useNavigate();
  const [schoolID, setSchoolID] = useState<string | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [hasValidClaims, setHasValidClaims] = useState(false);
  const [students, setStudents] = useState<Student[]>([]);
  const [classes, setClasses] = useState<ClassData[]>([]);
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [selectedForApproval, setSelectedForApproval] = useState<string[]>([]);
  const [promotionHistory, setPromotionHistory] = useState<PromotionHistory[]>([]);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [rules, setRules] = useState({
    minGradeAverage: 50,
    requireCoreSubjects: true,
    requireTeacherApproval: false,
  });
  const [disableEligibilityRules, setDisableEligibilityRules] = useState(false);

  const [scheduledDate, setScheduledDate] = useState<Date | undefined>();
  const [autoPromotionEnabled, setAutoPromotionEnabled] = useState(false);
  const [academicYear, setAcademicYear] = useState<string>("2025-2026");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedClass, setSelectedClass] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);
  const [revertDialogOpen, setRevertDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [pendingPromoteType, setPendingPromoteType] = useState<'all' | 'selected' | null>(null);
  const [revertIds, setRevertIds] = useState<string[]>([]);
  const [scheduleConfirmOpen, setScheduleConfirmOpen] = useState(false);
  const [pendingScheduleSettings, setPendingScheduleSettings] = useState<{ date: Date | undefined, enabled: boolean } | null>(null);

  // ============ PROMOTION SIMULATOR STATE ============
  const [simulatorStudentId, setSimulatorStudentId] = useState<string>("");
  const [simulatorHistory, setSimulatorHistory] = useState<string[]>([]); // Stack of class UIDs for back navigation
  const [isSimulating, setIsSimulating] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          await user.getIdToken(true);
          const idTokenResult = await user.getIdTokenResult(true);
          const affiliations = (idTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
          const defaultAffiliation = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;
          const claimSchoolID = idTokenResult.claims.schoolID as string | undefined;
          let selectedAffiliation: Affiliation | null = null;
          if (defaultAffiliation?.role === "admin" && defaultAffiliation.schoolID) {
            selectedAffiliation = defaultAffiliation;
          } else if (affiliations.length > 0) {
            selectedAffiliation = affiliations.find(aff => aff.role === "admin" && aff.schoolID) || affiliations[0];
          }
          if (selectedAffiliation?.role === "admin" && selectedAffiliation.schoolID && claimSchoolID === selectedAffiliation.schoolID) {
            setSchoolID(selectedAffiliation.schoolID);
            setHasValidClaims(true);
          } else {
            throw new Error("Invalid admin claims");
          }
        } catch (error) {
          console.error("Error verifying user claims:", error);
          toast.error("Missing Permissions: You lack the necessary permissions to manage promotions. Please contact support.", { duration: 5000 });
          navigate("/login-page");
        }
      } else {
        toast.error("Authentication Error: No user logged in. Please log in and try again.", { duration: 5000 });
        navigate("/login-page");
      }
      setIsAuthLoading(false);
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (!schoolID || !hasValidClaims) return;
    const settingsRef = doc(db, `schools/${schoolID}/settings/promotion`);
    const unsubscribe = onSnapshot(settingsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        const settings = data.promotionSettings || {};
        setRules(settings.rules || {
          minGradeAverage: 50,
          requireCoreSubjects: true,
          requireTeacherApproval: false,
        });
        setDisableEligibilityRules(!!settings.disableEligibilityRules);
      } else {
        setDoc(settingsRef, {
          promotionSettings: {
            rules: { minGradeAverage: 50, requireCoreSubjects: true, requireTeacherApproval: false },
            disableEligibilityRules: false,
          }
        }, { merge: true }).catch((error) => {
          console.error('Error creating defaults:', error);
        });
      }
    });
    return () => unsubscribe();
  }, [schoolID, hasValidClaims]);

  // Load scheduler settings
  useEffect(() => {
    if (!schoolID || !hasValidClaims) return;
    const settingsRef = doc(db, `schools/${schoolID}/settings/promotion`);
    const unsubscribe = onSnapshot(settingsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        const schedule = data.autoPromotionSchedule || {};
        const parsedDate = schedule.date ? new Date(schedule.date) : undefined;
        setScheduledDate(parsedDate);
        setAutoPromotionEnabled(!!schedule.enabled);
        if (schedule.academicYear) {
            setAcademicYear(schedule.academicYear);
        }
      }
    });
    return () => unsubscribe();
  }, [schoolID, hasValidClaims]);

  const saveScheduleSettings = async (date: Date | undefined, enabled: boolean) => {
    if (!schoolID || !hasValidClaims) return;
    try {
      const settingsRef = doc(db, `schools/${schoolID}/settings/promotion`);
      await setDoc(settingsRef, {
        autoPromotionSchedule: {
          enabled,
          date: date ? date.toISOString() : null,
          month: date ? date.getMonth() + 1 : null,
          day: date ? date.getDate() : null,
          hour: date ? date.getHours() : null, // Store hour for hourly checks
          updatedAt: new Date().toISOString(),
          updatedBy: auth.currentUser?.uid,
          academicYear: academicYear // Save the selected academic year
        }
      }, { merge: true });
      
      setScheduledDate(date);
      setAutoPromotionEnabled(enabled);
      
      if (enabled && date) {
        toast.success(`Auto-Promotion Scheduled: System will attempt to promote students on ${format(date, "PPP")} at ${format(date, "p")} for ${academicYear}.`, { duration: 5000, icon: <CalendarIcon className="h-5 w-5" /> });
      } else if (!enabled) {
         toast.info("Auto-Promotion Disabled.", { duration: 3000 });
      }
    } catch (error) {
      console.error("Error saving schedule:", error);
      toast.error("Failed to save schedule settings.");
    }
  };


  const handleSchedulePromotion = () => {
    if (!scheduledDate) {
      toast.error("Please select a valid date first.");
      return;
    }
    setPendingScheduleSettings({ date: scheduledDate, enabled: autoPromotionEnabled });
    setScheduleConfirmOpen(true);
  };

  const confirmScheduleSettings = () => {
    if (pendingScheduleSettings) {
       saveScheduleSettings(pendingScheduleSettings.date, pendingScheduleSettings.enabled);
       setScheduleConfirmOpen(false);
       setPendingScheduleSettings(null);
    }
  };

  useEffect(() => {
    if (!schoolID || !hasValidClaims) return;
    const classesRef = collection(db, `schools/${schoolID}/classes`);
    const unsubscribe = onSnapshot(classesRef, (snapshot) => {
      const classData = snapshot.docs.map(doc => ({
        classUid: doc.id,
        name: doc.data().name || '',
        level: Number(doc.data().level || 0),
        stream: doc.data().stream || '',
        namingConvention: doc.data().namingConvention || '',
        stage: doc.data().stage || doc.data().stageType, // Support both field names
        bridgeToGrade: doc.data().bridgeToGrade,
      })).sort((a, b) => a.name.localeCompare(b.name));
      console.log('Fetched classes:', classData);
      setClasses(classData);
      setIsDataLoading(false);
    }, (error) => {
      console.error("Error fetching classes:", error);
      toast.error(`Error Loading Classes: Failed to fetch classes: ${error.message}. Retrying...`, { duration: 5000 });
      setIsDataLoading(false);
    });
    return () => unsubscribe();
  }, [schoolID, hasValidClaims]);

  useEffect(() => {
    if (!schoolID || !hasValidClaims) return;
    const studentsRef = collection(db, `schools/${schoolID}/students`);
    const unsubscribe = onSnapshot(studentsRef, (snapshot) => {
      const studentData = snapshot.docs.map(doc => ({
        id: doc.id,
        registrationNumber: doc.data().registrationNumber || '',
        name: `${doc.data().firstName || ''} ${doc.data().middleName ? doc.data().middleName + ' ' : ''}${doc.data().lastName || ''}`,
        currentClass: doc.data().className || '',
        currentClassUid: doc.data().currentClassUid || '',
        gradeAverage: Number(doc.data().gradeAverage || 0),
        coreSubjectsPassed: Boolean(doc.data().coreSubjectsPassed || false),
        teacherApproval: Boolean(doc.data().teacherApproval || false),
        promoted: Boolean(doc.data().promoted || false),
        promotedDate: doc.data().promotedDate ? new Date(doc.data().promotedDate) : undefined,
        promotionYear: doc.data().promotionYear || undefined,
      }));
      console.log('Fetched students:', studentData);
      setStudents(studentData);
      setIsDataLoading(false);
    }, (error) => {
      console.error("Error fetching students:", error);
      toast.error(`Error Loading Students: Failed to fetch students: ${error.message}. Retrying...`, { duration: 5000 });
      setIsDataLoading(false);
    });
    return () => unsubscribe();
  }, [schoolID, hasValidClaims]);

  useEffect(() => {
    if (!schoolID || !hasValidClaims) return;
    const promotionHistoryRef = collection(db, `schools/${schoolID}/promotionHistory`);
    const unsubscribe = onSnapshot(promotionHistoryRef, (snapshot) => {
      const historyData = snapshot.docs.map(doc => ({
        id: doc.id,
        studentId: doc.data().studentId || '',
        registrationNumber: doc.data().registrationNumber || '',
        name: doc.data().name || '',
        fromClass: doc.data().fromClass || '',
        fromClassUid: doc.data().fromClassUid || '',
        toClass: doc.data().toClass || '',
        toClassUid: doc.data().toClassUid || '',
        promotionYear: doc.data().promotionYear || '',
        promotedDate: doc.data().promotedDate ? new Date(doc.data().promotedDate) : new Date(),
      }));
      console.log('Fetched promotion history:', historyData);
      setPromotionHistory(historyData);
    }, (error) => {
      console.error("Error fetching promotion history:", error);
      toast.error(`Error Loading Promotion History: Failed to fetch promotion history: ${error.message}. Retrying...`, { duration: 5000 });
    });
    return () => unsubscribe();
  }, [schoolID, hasValidClaims]);

  const checkEligibility = (student: Student) => {
    if (disableEligibilityRules) {
      console.log(`Disable mode: ${student.name} eligible=true`);
      return { eligible: true, reasons: [] as string[] };
    }
    const eligible =
      student.gradeAverage >= rules.minGradeAverage &&
      (!rules.requireCoreSubjects || student.coreSubjectsPassed) &&
      (!rules.requireTeacherApproval || student.teacherApproval) &&
      student.promotionYear !== academicYear;
    const reasons = [
      student.gradeAverage < rules.minGradeAverage && `Low grade average (${student.gradeAverage}% < ${rules.minGradeAverage}%)`,
      rules.requireCoreSubjects && !student.coreSubjectsPassed && "Failed core subjects",
      rules.requireTeacherApproval && !student.teacherApproval && "No teacher approval",
      student.promotionYear === academicYear && `Already promoted in ${academicYear}`,
    ].filter(Boolean) as string[];
    console.log(`Eligibility check for ${student.name}: eligible=${eligible}, reasons=${reasons.join(', ')}`);
    return { eligible, reasons };
  };

  // ============================================================================
  // PROMOTION LOGIC - KEEP IN SYNC WITH functions/src/promote.ts
  // ============================================================================
  // This logic MUST match the getTargetClasses function in the cloud function.
  // If you modify this, also update functions/src/promote.ts to match.
  // 
  // Algorithm:
  // 1. Look up current class by UID
  // 2. If nextClassUid exists, use it directly (HIGHEST PRIORITY)
  // 3. If nextClassUid is 'ALUMNI', return empty (handled by performPromotion)
  // 4. Progressive stage: Bridge to Level 1 of DIFFERENT naming convention
  // 5. Final stage: Graduate to Alumni
  // 6. Standard: Level + 1 within SAME naming convention
  // ============================================================================
  const getTargetClasses = (currentClassUid: string): ClassData[] => {
    console.log(`\n[getTargetClasses] ========== START ==========`);
    console.log(`[getTargetClasses] Input UID: "${currentClassUid}"`);

    // Step 1: Find current class by UID
    const currentClass = classes.find(cls => cls.classUid === currentClassUid);

    if (!currentClass) {
      console.log(`[getTargetClasses] ERROR: No class found with UID "${currentClassUid}"`);
      return [];
    }

    console.log(`[getTargetClasses] Found: "${currentClass.name}" (Level ${currentClass.level}, Stream "${currentClass.stream}")`);

    // Step 2: Check for Direct Pointer (nextClassUid) - HIGHEST PRIORITY
    if (currentClass.nextClassUid) {
      if (currentClass.nextClassUid === 'ALUMNI') {
        console.log(`[getTargetClasses] SUCCESS (Direct): "${currentClass.name}" -> ALUMNI`);
        return []; // Return empty for alumni - handled separately in performPromotion
      }
      const nextClass = classes.find(cls => cls.classUid === currentClass.nextClassUid);
      if (nextClass) {
        console.log(`[getTargetClasses] SUCCESS (Direct): "${currentClass.name}" -> "${nextClass.name}"`);
        return [nextClass];
      } else {
        console.log(`[getTargetClasses] WARNING: nextClassUid "${currentClass.nextClassUid}" not found in classes. Falling back to dynamic resolution...`);
      }
    }

    const currentStream = (currentClass.stream || "").toLowerCase().trim();
    const currentNaming = (currentClass.namingConvention || "").toLowerCase().trim();
    let candidates: ClassData[] = [];

    // Step 3: Find Candidates based on Stage Type
    const isFinalStage = currentClass.stage === "final" || currentClass.stageType === "final";
    const isProgressiveStage = currentClass.stage === "progressive" || currentClass.stageType === "progressive";

    if (isFinalStage) {
      // FINAL STAGE: Students graduate to Alumni
      console.log(`[getTargetClasses] Final stage detected for "${currentClass.name}"`);
      if (currentClass.bridgeToGrade) {
        // Explicit bridge to a specific grade
        candidates = classes.filter(cls =>
          cls.name === currentClass.bridgeToGrade || cls.name.startsWith(currentClass.bridgeToGrade + " ")
        );
      } else {
        console.log(`[getTargetClasses] SUCCESS (Final Stage): "${currentClass.name}" -> ALUMNI`);
        return []; // Alumni - handled separately
      }

    } else if (isProgressiveStage) {
      // PROGRESSIVE STAGE: Students move to Level 1 of a DIFFERENT naming convention
      // Example: "Pre Primary 2" (progressive) -> "Grade 1" (not Pre Primary 3!)
      console.log(`[getTargetClasses] Progressive stage detected for "${currentClass.name}"`);
      console.log(`[getTargetClasses] Looking for Level 1 classes with DIFFERENT naming convention than "${currentNaming}"`);

      // Find all Level 1 classes with a DIFFERENT naming convention
      candidates = classes.filter(cls => {
        const clsNaming = (cls.namingConvention || "").toLowerCase().trim();
        return cls.level === 1 && clsNaming !== currentNaming && clsNaming !== "";
      });

      console.log(`[getTargetClasses] Found ${candidates.length} Level 1 candidates with different naming`);
      candidates.forEach(c => console.log(`   - Candidate: "${c.name}" (naming: ${c.namingConvention})`));

      // If no candidates found, try bridgeToGrade as fallback
      if (candidates.length === 0 && currentClass.bridgeToGrade) {
        console.log(`[getTargetClasses] No Level 1 candidates, trying bridgeToGrade: "${currentClass.bridgeToGrade}"`);
        candidates = classes.filter(cls =>
          cls.name.toLowerCase().startsWith(currentClass.bridgeToGrade!.toLowerCase())
        );
      }

    } else {
      // STANDARD PROGRESSION: Level + 1 within SAME naming convention
      const nextLevel = currentClass.level + 1;
      console.log(`[getTargetClasses] Standard progression: Level ${currentClass.level} -> Level ${nextLevel} (same naming: "${currentNaming}")`);

      // First, find classes with same naming convention AND next level
      candidates = classes.filter(cls => {
        const clsNaming = (cls.namingConvention || "").toLowerCase().trim();
        return cls.level === nextLevel && clsNaming === currentNaming;
      });

      console.log(`[getTargetClasses] Found ${candidates.length} candidates with same naming`);

      // If no exact naming match, fall back to just level match (legacy support)
      if (candidates.length === 0) {
        console.log(`[getTargetClasses] No same-naming candidates, falling back to level-only match`);
        candidates = classes.filter(cls => cls.level === nextLevel);
      }
    }

    if (candidates.length === 0) {
      console.log(`[getTargetClasses] FAILED: No candidate classes found for next level/bridge.`);
      return [];
    }

    // Step 4: Try to find Exact Stream Match
    const matchingStream = candidates.find(cls => (cls.stream || "").toLowerCase().trim() === currentStream);
    if (matchingStream) {
      console.log(`[getTargetClasses] SUCCESS (Stream Match): "${currentClass.name}" -> "${matchingStream.name}"`);
      return [matchingStream];
    }

    // Step 5: No Stream Match -> Return ALL Candidates for Load Balancing
    console.log(`[getTargetClasses] No stream match ('${currentStream}'). Returning ${candidates.length} candidates for distribution.`);
    candidates.forEach(c => console.log(`   - Candidate: ${c.name} (${c.classUid})`));
    return candidates;
  };

  // Helper function to calculate final grade from average
  const calculateGrade = (average: number): string => {
    if (average >= 90) return 'A+';
    if (average >= 80) return 'A';
    if (average >= 75) return 'A-';
    if (average >= 70) return 'B+';
    if (average >= 65) return 'B';
    if (average >= 60) return 'B-';
    if (average >= 55) return 'C+';
    if (average >= 50) return 'C';
    if (average >= 40) return 'D';
    return 'E';
  };

  // Helper to calculate pending balance to snapshot
  const calculatePendingBalance = async (studentRegistrationNumber: string, currentClass: string) => {
    try {
      if (!schoolID) return 0;
      
      // 1. Get Fee Structure
      const feeStructureRef = doc(db, `schools/${schoolID}/deployedFeeStructures/current`);
      const feeStructureSnap = await getDoc(feeStructureRef);
      if (!feeStructureSnap.exists()) return 0;
      
      const feeStructure = feeStructureSnap.data();
      const levelFees = feeStructure.levelFees || [];
      
      // 2. Find matching level
      const matchingLevel = levelFees.find((level: any) => 
        level.classes && level.classes.some((c: string) => 
          c.toLowerCase() === currentClass.toLowerCase() ||
          currentClass.toLowerCase().includes(c.toLowerCase())
        )
      );
      
      let totalFees = 0;
      if (matchingLevel) {
        const categories = matchingLevel.categories || [];
        // Assume full annual fees for promotion snapshot (End of Year)
        categories.forEach((category: any) => {
           const v1 = Number(category.term1 || 0);
           const v2 = Number(category.term2 || 0);
           const v3 = Number(category.term3 || 0);
           totalFees += (v1 + v2 + v3);
        });
        
        // Handle Government Sponsorship (General logic)
        if (matchingLevel.isGovernmentSponsored && matchingLevel.governmentSponsorshipAmount) {
             const govAmount = Number(matchingLevel.governmentSponsorshipAmount) || 0;
             totalFees = Math.max(0, totalFees - govAmount);
        }
      }
      
      // 3. Get Payments
      let paidAmount = 0;
      const providers = ['mpesa', 'equity', 'kcb', 'cash', 'card', 'bank'];
      // Note: We sum ALL payments here because "outstandingBalance" snapshot 
      // is intended to consolidate ALL history up to this point.
      for (const provider of providers) {
        const txQuery = query(
          collection(db, `schools/${schoolID}/transactions/${provider}/records`),
          where('accountReference', '==', `Fee-${studentRegistrationNumber}`),
          where('status', '==', 'completed')
        );
        const txSnap = await getDocs(txQuery);
        txSnap.forEach(doc => {
           paidAmount += Number(doc.data().amount || 0);
        });
      }
      
      // 4. Calculate Balance
      // Include any PREVIOUSLY snapshotted balance if it exists?
      // For safety, let's assume 'totalFees' is just for the current year.
      // If the student ALREADY has 'outstandingBalance' from a previous year, we should add it.
      // But we can't easily distinguish 'paid for old' vs 'paid for new' without the year logic.
      // 
      // SIMPLE APPROACH for "Owe Nothing More":
      // Balance = (Fees for Class That Just Ended) - (Payments Made).
      // We will ADD this to any existing 'outstandingBalance' in the update step, 
      // but here we just return the calculation for the *current* class context.
      
      return Math.max(0, totalFees - paidAmount);
      
    } catch (e) {
      console.error("Error calculating balance snapshot:", e);
      return 0; // Fail safe
    }
  };

  const performPromotion = async (studentIds: string[]) => {
    if (!schoolID || !hasValidClaims) {
      toast.error("Authentication Error: Missing permissions or login.", { duration: 5000 });
      return;
    }
    const toPromote = students.filter(s => studentIds.includes(s.id));
    const eligibleToPromote = toPromote.filter(s => checkEligibility(s).eligible);
    if (eligibleToPromote.length === 0) {
      toast.error("No Eligible Students: No selected students are eligible for promotion. Check eligibility rules or select different students.", { duration: 5000 });
      return;
    }
    
    // Group students by current class UID to handle batch distribution
    const studentsByClass: Record<string, Student[]> = {};
    for (const s of eligibleToPromote) {
        if (!studentsByClass[s.currentClassUid]) studentsByClass[s.currentClassUid] = [];
        studentsByClass[s.currentClassUid].push(s);
    }

    try {
      const studentsRef = collection(db, `schools/${schoolID}/students`);
      const historyRef = collection(db, `schools/${schoolID}/promotionHistory`);
      const alumniRef = collection(db, `schools/${schoolID}/alumni`);
      const promoted: Student[] = [];
      const graduated: Student[] = [];
      const promotedDetails: { registrationNumber: string; name: string; from: string; fromUid: string; to: string }[] = [];
      const graduatedDetails: { registrationNumber: string; name: string; from: string; finalGrade: string }[] = [];
      const skipped: string[] = [];

      for (const [classUid, classStudents] of Object.entries(studentsByClass)) {
          const currentClass = classes.find(c => c.classUid === classUid);
          const targets = getTargetClasses(classUid);
          
          // Check if this is a final stage class (should graduate to alumni)
          const isFinalStage = currentClass?.stage === 'final' || currentClass?.nextClassUid === 'ALUMNI';
          
          if (targets.length === 0) {
            if (isFinalStage) {
              // Graduate students to Alumni
              console.log(`[performPromotion] Graduating students from final class ${currentClass?.name} to Alumni`);
              
              for (const student of classStudents) {
                if (student.promotionYear === academicYear) {
                  skipped.push(student.name);
                  continue;
                }

                const promotedDate = new Date();
                const finalGrade = calculateGrade(student.gradeAverage || 0);

                // Write to Alumni
                await setDoc(doc(alumniRef, student.id), {
                  studentId: student.id,
                  name: student.name,
                  registrationNumber: student.registrationNumber,
                  alumniYear: academicYear,
                  className: student.currentClass,
                  finalGrade: finalGrade,
                  gradeAverage: student.gradeAverage || 0,
                  graduatedAt: promotedDate.toISOString()
                });

                // Update student
                await setDoc(doc(studentsRef, student.id), {
                  status: 'Alumni',
                  className: 'Alumni',
                  currentClassUid: 'ALUMNI',
                  promoted: true,
                  promotedDate: promotedDate.toISOString(),
                  promotionYear: academicYear,
                }, { merge: true });

                const historyId = `${student.id}_${promotedDate.toISOString()}`;
                await setDoc(doc(historyRef, historyId), {
                  studentId: student.id,
                  registrationNumber: student.registrationNumber,
                  name: student.name,
                  fromClass: student.currentClass,
                  fromClassUid: student.currentClassUid,
                  toClass: 'Alumni',
                  toClassUid: 'ALUMNI',
                  promotionYear: academicYear,
                  promotedDate: promotedDate.toISOString(),
                  isGraduation: true
                });

                graduated.push({
                  ...student,
                  currentClass: 'Alumni',
                  currentClassUid: 'ALUMNI',
                  promoted: true,
                  promotedDate,
                  promotionYear: academicYear,
                });
                graduatedDetails.push({
                  registrationNumber: student.registrationNumber,
                  name: student.name,
                  from: student.currentClass,
                  finalGrade
                });
              }
            } else {
              classStudents.forEach(s => skipped.push(s.name));
            }
            continue;
          }

          // Sort targets
          targets.sort((a, b) => a.name.localeCompare(b.name));

          // Distribute
          for (let i = 0; i < classStudents.length; i++) {
            const student = classStudents[i];
            if (student.promotionYear === academicYear) {
              skipped.push(student.name);
              continue;
            }

            // Round-robin assignment
            const targetClass = targets[i % targets.length];

            console.log(`[performPromotion] Promoting ${student.name} from ${student.currentClass} to ${targetClass.name}`);
            
            // --- SNAPSHOT DEBT LOGIC ---
            // Calculate what they owe for the class they are LEAVING
            const debtSnapshot = await calculatePendingBalance(student.registrationNumber, student.currentClass);
            // ---------------------------

            const promotedDate = new Date();
            const existingHistory = promotionHistory.filter(h => h.studentId === student.id && h.promotionYear === academicYear);
            for (const history of existingHistory) {
              await deleteDoc(doc(historyRef, history.id));
            }

            // Perform Update: New Class + Snapshot Debt
            // Note: We use { merge: true }, but we want to Increment outstandingBalance if it exists.
            // Since we can't trigger atomic increment easily without getting the doc first, and we calculated 'debtSnapshot'
            // as (Total Fees - Total Paid), this 'debtSnapshot' actually REPRESENTS the specific debt for this class.
            // If the student has "Previous" outstandingBalance, we should Ideally add to it.
            // But 'calculatePendingBalance' subtracts ALL payments.
            // If we assume payments covered previous debt first, then 'Total Debt' = 'debtSnapshot'.
            // So setting outstandingBalance = debtSnapshot is effectively "Resetting the Ledger" to this single Number.
            // This works perfectly with the "Clean Slate" model for the new year.
            
            await setDoc(doc(studentsRef, student.id), {
              className: targetClass.name,
              currentClassUid: targetClass.classUid,
              promoted: true,
              promotedDate: promotedDate.toISOString(),
              promotionYear: academicYear,
              // Update debt
              outstandingBalance: debtSnapshot
            }, { merge: true });

            const historyId = `${student.id}_${promotedDate.toISOString()}`;
            await setDoc(doc(historyRef, historyId), {
              studentId: student.id,
              registrationNumber: student.registrationNumber,
              name: student.name,
              fromClass: student.currentClass,
              fromClassUid: student.currentClassUid,
              toClass: targetClass.name,
              toClassUid: targetClass.classUid,
              promotionYear: academicYear,
              promotedDate: promotedDate.toISOString(),
              balanceSnapshot: debtSnapshot // Record this for audit
            });
            
            const updatedStudent = {
              ...student,
              currentClass: targetClass.name,
              currentClassUid: targetClass.classUid,
              promoted: true,
              promotedDate,
              promotionYear: academicYear,
            };
            promoted.push(updatedStudent);
            promotedDetails.push({ 
                registrationNumber: student.registrationNumber, 
                name: student.name, 
                from: student.currentClass,
                fromUid: student.currentClassUid,
                to: targetClass.name 
            });
          }
      }

      // Update local state - include both promoted and graduated students
      const allUpdated = [...promoted, ...graduated];
      setStudents(students.map(s => allUpdated.find(p => p.id === s.id) || s));
      setPromotionHistory(prev => [
        ...prev.filter(h => !allUpdated.some(p => p.id === h.studentId && h.promotionYear === academicYear)),
        ...promoted.map((p, idx) => ({
          id: `${p.id}_${p.promotedDate!.toISOString()}`,
          studentId: p.id,
          registrationNumber: p.registrationNumber,
          name: p.name,
          fromClass: promotedDetails[idx].from,
          fromClassUid: promotedDetails[idx].fromUid,
          toClass: promotedDetails[idx].to,
          toClassUid: p.currentClassUid,
          promotionYear: academicYear,
          promotedDate: p.promotedDate!,
        })),
        ...graduated.map((g, idx) => ({
          id: `${g.id}_${g.promotedDate!.toISOString()}`,
          studentId: g.id,
          registrationNumber: g.registrationNumber,
          name: g.name,
          fromClass: graduatedDetails[idx].from,
          fromClassUid: g.currentClassUid,
          toClass: 'Alumni',
          toClassUid: 'ALUMNI',
          promotionYear: academicYear,
          promotedDate: g.promotedDate!,
        }))
      ]);
      setSelectedStudents([]);

      // Show success message with graduation info
      if (graduated.length > 0 && promoted.length > 0) {
        toast.success(
          `Promotion Completed: Promoted ${promoted.length} student(s) and graduated ${graduated.length} to Alumni. Skipped ${skipped.length}.`,
          { duration: 8000, icon: <GraduationCap className="h-5 w-5" /> }
        );
      } else if (graduated.length > 0) {
        toast.success(
          `Graduation Completed: ${graduated.length} student(s) have graduated to Alumni! 🎓`,
          { duration: 8000, icon: <GraduationCap className="h-5 w-5" /> }
        );
      } else if (promoted.length > 0) {
        toast.success(
          `Promotion Completed: Successfully promoted ${promoted.length} student(s). Skipped ${skipped.length}.`,
          { duration: promotedDetails.length > 10 ? 10000 : 7000, icon: <TrendingUp className="h-5 w-5" /> }
        );
      } else if (skipped.length > 0) {
        toast.error(`Promotion Failed: No students could be promoted. Skipped ${skipped.length} student(s).`, { duration: 5000 });
      }
    } catch (error) {
      console.error("[performPromotion] Error promoting students:", error);
      toast.error(`Promotion Failed: ${(error as Error).message || "An unexpected error occurred."}`, { duration: 5000 });
    }
  };

  const handlePromoteSelected = () => {
    if (selectedStudents.length === 0) return;
    setPendingPromoteType('selected');
    setPromoteDialogOpen(true);
  };

  const handlePromoteAll = () => {
    const eligible = students.filter(s => s.promotionYear !== academicYear && checkEligibility(s).eligible);
    if (eligible.length === 0) {
      toast.error("No Eligible Students: No students are eligible for promotion based on current rules. Try adjusting rules or enabling 'Disable Eligibility Rules'.", { duration: 5000 });
      return;
    }
    setPendingPromoteType('all');
    setPromoteDialogOpen(true);
  };

  const confirmPromote = () => {
    if (pendingPromoteType === 'all') {
      const eligible = students.filter(s => s.promotionYear !== academicYear && checkEligibility(s).eligible);
      performPromotion(eligible.map(s => s.id));
    } else if (pendingPromoteType === 'selected') {
      performPromotion(selectedStudents);
    }
    setPromoteDialogOpen(false);
    setPendingPromoteType(null);
  };

  const handleRevertPromotion = (studentIds: string[]) => {
    setRevertIds(studentIds);
    setRevertDialogOpen(true);
  };

  const confirmRevert = async () => {
    if (!schoolID || !hasValidClaims || revertIds.length === 0) {
      toast.error("Authentication Error: Missing permissions or login.", { duration: 5000 });
      setRevertDialogOpen(false);
      return;
    }
    
    try {
      const studentsRef = collection(db, `schools/${schoolID}/students`);
      const historyRef = collection(db, `schools/${schoolID}/promotionHistory`);
      const alumniRef = collection(db, `schools/${schoolID}/alumni`);
      const revertedDetails: { registrationNumber: string; name: string; from: string; to: string }[] = [];
      
      for (const studentId of revertIds) {
        const historyEntry = promotionHistory.find(h => h.studentId === studentId && h.promotionYear === academicYear);
        if (historyEntry) {
          const student = students.find(s => s.id === studentId);
          if (student) {
            // Check if this was a graduation (to Alumni)
            const wasGraduation = historyEntry.toClass === 'Alumni' || historyEntry.toClassUid === 'ALUMNI';
            
            // Revert student to previous class
            await setDoc(doc(studentsRef, studentId), {
              className: historyEntry.fromClass,
              currentClassUid: historyEntry.fromClassUid,
              promoted: false,
              promotedDate: null,
              promotionYear: null,
              // Reset status if was Alumni
              ...(wasGraduation && { status: 'active' })
            }, { merge: true });
            
            // Delete from promotion history
            await deleteDoc(doc(historyRef, historyEntry.id));
            
            // If was graduated, also remove from alumni collection
            if (wasGraduation) {
              try {
                await deleteDoc(doc(alumniRef, studentId));
                console.log(`[confirmRevert] Removed ${student.name} from alumni collection`);
              } catch (alumniDeleteError) {
                console.warn(`[confirmRevert] Could not delete alumni record for ${studentId}:`, alumniDeleteError);
              }
            }
            
            revertedDetails.push({ registrationNumber: student.registrationNumber, name: student.name, from: student.currentClass, to: historyEntry.fromClass });
          }
        }
      }
      setStudents(students.map(s => {
        const historyEntry = promotionHistory.find(h => h.studentId === s.id && h.promotionYear === academicYear);
        if (historyEntry && revertIds.includes(s.id)) {
          return { ...s, currentClass: historyEntry.fromClass, currentClassUid: historyEntry.fromClassUid, promoted: false, promotedDate: undefined, promotionYear: undefined };
        }
        return s;
      }));
      setPromotionHistory(promotionHistory.filter(h => !revertIds.includes(h.studentId) || h.promotionYear !== academicYear));
      toast.success(
        `Promotions Reverted: Successfully reverted ${revertedDetails.length} student(s).`,
        { duration: revertedDetails.length > 10 ? 10000 : 7000, icon: <Undo2 className="h-5 w-5" /> }
      );
    } catch (error) {
      console.error("[confirmRevert] Error reverting promotions:", error);
      toast.error(`Revert Failed: ${(error as Error).message || "An unexpected error occurred."}`, { duration: 5000 });
    } finally {
      setRevertDialogOpen(false);
      setRevertIds([]);
    }
  };

  const handleApprovePromotions = (studentIds: string[]) => {
    if (!schoolID || !hasValidClaims || studentIds.length === 0) {
      toast.error("Authentication Error: Missing permissions or login.", { duration: 5000 });
      setApproveDialogOpen(false);
      return;
    }
    setSelectedForApproval(studentIds);
    setApproveDialogOpen(true);
  };

  const confirmApprove = async () => {
    if (!schoolID || !hasValidClaims || selectedForApproval.length === 0) {
      toast.error("Authentication Error: Missing permissions or login.", { duration: 5000 });
      setApproveDialogOpen(false);
      return;
    }
    try {
      const studentsRef = collection(db, `schools/${schoolID}/students`);
      const historyRef = collection(db, `schools/${schoolID}/promotionHistory`);
      const approvedDetails: { registrationNumber: string; name: string; class: string }[] = [];
      for (const studentId of selectedForApproval) {
        const student = students.find(s => s.id === studentId);
        if (student) {
          await setDoc(doc(studentsRef, studentId), {
            promoted: false,
            promotedDate: null,
            promotionYear: null,
          }, { merge: true });
          const historyEntries = promotionHistory.filter(h => h.studentId === studentId && h.promotionYear === academicYear);
          for (const history of historyEntries) {
            await deleteDoc(doc(historyRef, history.id));
          }
          approvedDetails.push({ registrationNumber: student.registrationNumber, name: student.name, class: student.currentClass });
        }
      }
      setStudents(students.map(s =>
        selectedForApproval.includes(s.id)
          ? { ...s, promoted: false, promotedDate: undefined, promotionYear: undefined }
          : s
      ));
      setPromotionHistory(promotionHistory.filter(h => !selectedForApproval.includes(h.studentId) || h.promotionYear !== academicYear));
      toast.success(
        `Promotions Approved: Successfully finalized promotions for ${approvedDetails.length} student(s). They are now eligible for future promotions.`,
        { duration: approvedDetails.length > 10 ? 10000 : 7000, icon: <CheckCircle2 className="h-5 w-5" /> }
      );
    } catch (error) {
      console.error("[confirmApprove] Error approving promotions:", error);
      toast.error(`Approval Failed: ${(error as Error).message || "An unexpected error occurred."}`, { duration: 5000 });
    } finally {
      setApproveDialogOpen(false);
      setSelectedForApproval([]);
    }
  };

  // Auto-approve all promoted students (for testing or bulk approval)
  // This follows the same logic as confirmApprove - clears promotionYear and deletes history
  const handleAutoApproveAll = async () => {
    if (!schoolID || !hasValidClaims) {
      toast.error("Authentication Error: Missing permissions or login.", { duration: 5000 });
      return;
    }

    // Find all students who were promoted this academic year
    const promotedStudentsList = students.filter(s => s.promotionYear === academicYear);

    if (promotedStudentsList.length === 0) {
      toast.info("No promoted students found for this academic year.", { duration: 5000 });
      return;
    }

    try {
      const studentsRef = collection(db, `schools/${schoolID}/students`);
      const historyRef = collection(db, `schools/${schoolID}/promotionHistory`);
      let approvedCount = 0;

      for (const student of promotedStudentsList) {
        // Clear promotion tracking fields (same as confirmApprove)
        await setDoc(doc(studentsRef, student.id), {
          promoted: false,
          promotedDate: null,
          promotionYear: null,
        }, { merge: true });

        // Delete promotion history entries for this student
        const historyEntries = promotionHistory.filter(h => h.studentId === student.id && h.promotionYear === academicYear);
        for (const history of historyEntries) {
          await deleteDoc(doc(historyRef, history.id));
        }
        approvedCount++;
      }

      // Update local state
      setStudents(students.map(s =>
        s.promotionYear === academicYear
          ? { ...s, promoted: false, promotedDate: undefined, promotionYear: undefined }
          : s
      ));
      setPromotionHistory(promotionHistory.filter(h => h.promotionYear !== academicYear));

      toast.success(
        `Auto-Approval Complete: Finalized ${approvedCount} promotion(s). Students are now in their new classes and eligible for future promotions.`,
        { duration: 8000, icon: <CheckCircle2 className="h-5 w-5 text-green-600" /> }
      );
    } catch (error) {
      console.error("[handleAutoApproveAll] Error approving promotions:", error);
      toast.error(`Approval Failed: ${(error as Error).message || "An unexpected error occurred."}`, { duration: 5000 });
    }
  };


  // ============ PROMOTION SIMULATOR FUNCTIONS ============
  const getSelectedSimulatorStudent = () => {
    return students.find(s => s.id === simulatorStudentId);
  };

  const getSimulatorCurrentClass = () => {
    const student = getSelectedSimulatorStudent();
    if (!student) return null;
    return classes.find(c => c.classUid === student.currentClassUid);
  };

  const simulateNext = async () => {
    if (!schoolID || !simulatorStudentId) return;
    
    const student = getSelectedSimulatorStudent();
    if (!student) {
      toast.error("Please select a student first.");
      return;
    }

    const currentClass = getSimulatorCurrentClass();
    if (!currentClass) {
      toast.error(`Cannot find class for student. UID: ${student.currentClassUid}`);
      return;
    }

    setIsSimulating(true);
    try {
      // Call cloud function instead of direct Firestore write
      const functions = getFunctions();
      const simulatePromotionFn = httpsCallable(functions, 'simulatePromotion');
      
      const result = await simulatePromotionFn({
        studentId: simulatorStudentId,
        direction: 'next'
      }) as any;
      
      if (result.data.success) {
        // Save current class to history stack for "Back" function
        setSimulatorHistory(prev => [...prev, currentClass.classUid]);
        toast.success(result.data.message);
      } else if (result.data.isEndOfChain) {
        toast.info(result.data.message);
      } else {
        toast.error(result.data.message || "Simulation failed");
      }
    } catch (error: any) {
      console.error("Simulate Next Error:", error);
      toast.error(`Simulation failed: ${error.message}`);
    } finally {
      setIsSimulating(false);
    }
  };

  const simulateBack = async () => {
    if (!schoolID || !simulatorStudentId) return;
    
    const student = getSelectedSimulatorStudent();
    if (!student) {
      toast.error("Please select a student first.");
      return;
    }

    if (simulatorHistory.length === 0) {
      toast.warning("No history to go back to. This is the starting class.");
      return;
    }

    setIsSimulating(true);
    try {
      // Pop the last class from history
      const previousClassUid = simulatorHistory[simulatorHistory.length - 1];

      // Call cloud function instead of direct Firestore write
      const functions = getFunctions();
      const simulatePromotionFn = httpsCallable(functions, 'simulatePromotion');
      
      const result = await simulatePromotionFn({
        studentId: simulatorStudentId,
        direction: 'back',
        previousClassUid: previousClassUid
      }) as any;
      
      if (result.data.success) {
        // Update history after successful cloud operation
        setSimulatorHistory(prev => prev.slice(0, -1));
        toast.success(result.data.message);
      } else {
        toast.error(result.data.message || "Revert failed");
      }
    } catch (error: any) {
      console.error("Simulate Back Error:", error);
      toast.error(`Revert failed: ${error.message}`);
    } finally {
      setIsSimulating(false);
    }
  };


  const uniqueClasses = Array.from(new Set(classes.map(cls => cls.name))).sort();
  const filteredStudents = students.filter(student => {
    const matchesClass = selectedClass === "all" || student.currentClass === selectedClass;
    const matchesSearch = searchQuery === "" ||
      student.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.registrationNumber.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesClass && matchesSearch;
  });
  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedStudents = filteredStudents.slice(startIndex, endIndex);
  const eligibleStudents = useMemo(() => {
    const eligible = students.filter(s => s.promotionYear !== academicYear && checkEligibility(s).eligible);
    const nonPromotedCount = students.filter(s => s.promotionYear !== academicYear).length;
    console.log(`Computed eligible: ${eligible.length}/${students.length} (disable: ${disableEligibilityRules}, non-promoted: ${nonPromotedCount})`);
    return eligible;
  }, [students, disableEligibilityRules, rules, academicYear]);
  const flaggedStudents = useMemo(() => students.filter(s => s.promotionYear !== academicYear && !checkEligibility(s).eligible), [students, disableEligibilityRules, rules, academicYear]);
  const promotedStudents = useMemo(() => students.filter(s => s.promotionYear === academicYear), [students, academicYear]);

  if (isAuthLoading || isDataLoading) {
    return (
      <>
        <div className="fixed inset-0 bg-[#FFFFFF] dark:bg-[#020817] overflow-hidden">
          <SidebarProvider className="h-full overflow-hidden">
            <div className="flex h-full w-full">
              <AdminSidebar onSectionChange={() => {}} className="bg-[#FFFFFF] dark:bg-[#020817] border-r border-[#E2E8F0] dark:border-[#1E293B]" />
              <div className="flex flex-col flex-1 h-full min-w-0">
                <Header className="bg-[#FFFFFF] dark:bg-[#020817] border-b border-[#E2E8F0] dark:border-[#1E293B]" />
                <main className="flex-1 min-h-0 overflow-auto p-3 sm:p-6 flex flex-col justify-center items-center gap-6">
                  <div className="relative">
                    <div className="h-20 w-20 rounded-full border-4 border-blue-500/10 border-t-blue-600 animate-spin"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <TrendingUp className="h-8 w-8 text-blue-600 animate-pulse" />
                    </div>
                  </div>
                  <div className="animate-in fade-in zoom-in duration-700">
                    <p className="text-xs font-medium uppercase tracking-widest text-gray-400 dark:text-gray-500">Initializing Session</p>
                  </div>
                </main>
              </div>
            </div>
          </SidebarProvider>
        </div>
      </>
    );
  }

  if (!hasValidClaims) {
    return (
      <>
        <div className="fixed inset-0 bg-[#FFFFFF] dark:bg-[#020817] overflow-hidden">
          <SidebarProvider className="h-full overflow-hidden">
            <div className="flex h-full w-full">
              <AdminSidebar onSectionChange={() => {}} className="bg-[#FFFFFF] dark:bg-[#020817] border-r border-[#E2E8F0] dark:border-[#1E293B]" />
              <div className="flex flex-col flex-1 h-full min-w-0">
                <Header className="bg-[#FFFFFF] dark:bg-[#020817] border-b border-[#E2E8F0] dark:border-[#1E293B]" />
                <main className="flex-1 min-h-0 overflow-auto p-3 sm:p-6 flex justify-center items-center">
                  <Card className="w-full max-w-md m-2 bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B]">
                    <CardHeader>
                      <CardTitle className="text-lg sm:text-xl text-gray-900 dark:text-white">
                        Access Denied
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 dark:text-gray-400">
                        You do not have the necessary permissions to manage promotions. Please contact support or re-login.
                      </p>
                      <Button
                        onClick={() => navigate("/login-page")}
                        className="mt-4 bg-blue-600 text-white hover:bg-blue-700 m-2"
                      >
                        Go to Login
                      </Button>
                    </CardContent>
                  </Card>
                </main>
              </div>
            </div>
          </SidebarProvider>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="fixed inset-0 bg-[#FFFFFF] dark:bg-[#020817] overflow-hidden">
        <SidebarProvider className="h-full overflow-hidden">
          <div className="flex h-full w-full">
            <AdminSidebar onSectionChange={() => {}} className="bg-[#FFFFFF] dark:bg-[#020817] border-r border-[#E2E8F0] dark:border-[#1E293B]" />
            <div className="flex flex-col flex-1 h-full min-w-0">
              <Header className="bg-[#FFFFFF] dark:bg-[#020817] border-b border-[#E2E8F0] dark:border-[#1E293B]" />
              <main className="flex-1 min-h-0 overflow-auto p-2 sm:p-4 lg:p-6">
                <div className="mb-4 sm:mb-6">
              <div className="flex items-center justify-between mb-4">
                  <Button onClick={onBack} variant="outline" className="flex items-center gap-2 bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B] m-2">
                    <ArrowLeft className="h-4 w-4" />
                    Back to Classes
                  </Button>
                  <Button 
                    onClick={() => navigate('/school-alumni')}
                    variant="outline"
                    className="flex items-center gap-2 bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B] m-2"
                  >
                    <GraduationCap className="h-4 w-4" />
                    School Alumni
                  </Button>
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-foreground mb-2">Student Promotion System</h1>
              <p className="text-sm sm:text-base text-muted-foreground">
                Manage student promotions with eligibility rules, preview, and scheduling
              </p>
            </div>
            <Tabs defaultValue="preview" className="space-y-6 max-w-full">
              <TabsList className="flex flex-wrap items-center w-full max-w-full h-auto p-1 sm:p-1.5 bg-gray-100/50 dark:bg-[#0f172a] border border-[#E2E8F0] dark:border-[#1E293B] rounded-xl sm:rounded-2xl gap-0.5 sm:gap-1">
                <TabsTrigger 
                  value="preview" 
                  className="flex-1 min-w-[40px] sm:min-w-0 flex items-center justify-center gap-1 sm:gap-2 h-9 sm:h-11 px-2 sm:px-4 py-1.5 sm:py-2 transition-all duration-300 rounded-lg sm:rounded-xl font-semibold data-[state=active]:bg-white dark:data-[state=active]:bg-[#1e293b] data-[state=active]:text-blue-600 data-[state=active]:shadow-[0_4px_12px_rgba(53,118,223,0.15)] data-[state=active]:border-transparent border border-transparent"
                >
                  <CheckCircle2 className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:inline text-xs sm:text-sm truncate">Preview</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="rules" 
                  className="flex-1 min-w-[40px] sm:min-w-0 flex items-center justify-center gap-1 sm:gap-2 h-9 sm:h-11 px-2 sm:px-4 py-1.5 sm:py-2 transition-all duration-300 rounded-lg sm:rounded-xl font-semibold data-[state=active]:bg-white dark:data-[state=active]:bg-[#1e293b] data-[state=active]:text-amber-500 data-[state=active]:shadow-[0_4px_12px_rgba(245,158,11,0.15)] data-[state=active]:border-transparent border border-transparent"
                >
                  <Settings className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:inline text-xs sm:text-sm truncate">Rules</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="schedule" 
                  className="flex-1 min-w-[40px] sm:min-w-0 flex items-center justify-center gap-1 sm:gap-2 h-9 sm:h-11 px-2 sm:px-4 py-1.5 sm:py-2 transition-all duration-300 rounded-lg sm:rounded-xl font-semibold data-[state=active]:bg-white dark:data-[state=active]:bg-[#1e293b] data-[state=active]:text-indigo-600 data-[state=active]:shadow-[0_4px_12px_rgba(99,102,241,0.15)] data-[state=active]:border-transparent border border-transparent"
                >
                  <CalendarIcon className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:inline text-xs sm:text-sm truncate">Schedule</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="revert" 
                  className="flex-1 min-w-[40px] sm:min-w-0 flex items-center justify-center gap-1 sm:gap-2 h-9 sm:h-11 px-2 sm:px-4 py-1.5 sm:py-2 transition-all duration-300 rounded-lg sm:rounded-xl font-semibold data-[state=active]:bg-white dark:data-[state=active]:bg-[#1e293b] data-[state=active]:text-rose-600 data-[state=active]:shadow-[0_4px_12px_rgba(225,29,72,0.15)] data-[state=active]:border-transparent border border-transparent"
                >
                  <Undo2 className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:inline text-xs sm:text-sm truncate">Revert</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="simulator" 
                  className="flex-1 min-w-[40px] sm:min-w-0 flex items-center justify-center gap-1 sm:gap-2 h-9 sm:h-11 px-2 sm:px-4 py-1.5 sm:py-2 transition-all duration-300 rounded-lg sm:rounded-xl font-semibold data-[state=active]:bg-white dark:data-[state=active]:bg-[#1e293b] data-[state=active]:text-purple-600 data-[state=active]:shadow-[0_4px_12px_rgba(147,51,234,0.15)] data-[state=active]:border-transparent border border-transparent"
                >
                  <TrendingUp className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:inline text-xs sm:text-sm truncate">Simulator</span>
                </TabsTrigger>
              </TabsList>
              <TabsContent value="preview" className="space-y-3 sm:space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                  <Card className="m-2 bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B]">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium">Eligible</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-green-600">{eligibleStudents.length}</div>
                      <p className="text-xs text-muted-foreground">Ready to promote</p>
                    </CardContent>
                  </Card>
                  <Card className="m-2 bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B]">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium">Flagged</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-orange-600">{flaggedStudents.length}</div>
                      <p className="text-xs text-muted-foreground">Need review</p>
                    </CardContent>
                  </Card>
                  <Card className="m-2 bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B]">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium">Promoted</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-primary">{promotedStudents.length}</div>
                      <p className="text-xs text-muted-foreground">Already promoted</p>
                    </CardContent>
                  </Card>
                </div>
                <Card className="m-2 bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B]">
                  <CardHeader>
                    <CardTitle>Promotion Preview</CardTitle>
                    <CardDescription>Review student eligibility before confirming promotions</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {eligibleStudents.length === 0 && students.length > 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <AlertCircle className="h-8 w-8 mx-auto mb-2 text-orange-600" />
                        <p>All students are already promoted or ineligible. Check data or rules.</p>
                      </div>
                    ) : eligibleStudents.length === 0 && students.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <AlertCircle className="h-8 w-8 mx-auto mb-2 text-orange-600" />
                        <p>No students enrolled yet. Add students to promote.</p>
                      </div>
                    ) : (
                      <>
                        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-3 sm:mb-4">
                          <Button
                            onClick={handlePromoteAll}
                            disabled={eligibleStudents.length === 0}
                            className="flex items-center justify-center gap-2 w-full sm:w-auto text-sm bg-blue-600 hover:bg-blue-700 text-white m-2"
                          >
                            <TrendingUp className="h-4 w-4 shrink-0" />
                            <span className="truncate">Promote All ({eligibleStudents.length})</span>
                          </Button>
                          <Button
                            onClick={handlePromoteSelected}
                            disabled={selectedStudents.length === 0}
                            variant="outline"
                            className="flex items-center justify-center gap-2 w-full sm:w-auto text-sm bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B] m-2"
                          >
                            <CheckCircle2 className="h-4 w-4 shrink-0" />
                            <span className="truncate">Promote Selected ({selectedStudents.length})</span>
                          </Button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
                          <div className="space-y-2">
                            <Label htmlFor="search">Search Students</Label>
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                              <Input
                                id="search"
                                placeholder="Search by name or reg no..."
                                value={searchQuery}
                                onChange={(e) => {
                                  setSearchQuery(e.target.value);
                                  setCurrentPage(1);
                                }}
                                className="pl-9 bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B]"
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="classFilter">Filter by Class</Label>
                            <Select
                              value={selectedClass}
                              onValueChange={(value) => {
                                setSelectedClass(value);
                                setCurrentPage(1);
                              }}
                            >
                              <SelectTrigger id="classFilter" className="bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B]">
                                <SelectValue placeholder="Select a class" />
                              </SelectTrigger>
                              <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B]">
                                <SelectItem value="all">All Classes</SelectItem>
                                {uniqueClasses.map((className) => (
                                  <SelectItem key={className} value={className}>
                                    {className}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg overflow-hidden max-w-full">
                          <div className="overflow-x-auto max-w-full">
                            <Table className="min-w-full bg-[#FFFFFF] dark:bg-[#020817]">
                              <TableHeader>
                                <TableRow className="border-b border-[#E2E8F0] dark:border-[#1E293B]">
                                  <TableHead className="w-12">
                                    <Checkbox
                                      checked={selectedStudents.length === students.filter(s => s.promotionYear !== academicYear).length}
                                      onCheckedChange={(checked) => {
                                        if (checked) {
                                          setSelectedStudents(students.filter(s => s.promotionYear !== academicYear).map(s => s.id));
                                        } else {
                                          setSelectedStudents([]);
                                        }
                                      }}
                                    />
                                  </TableHead>
                                  <TableHead className="w-32">Reg No.</TableHead>
                                  <TableHead>Student Name</TableHead>
                                  <TableHead>Current Class</TableHead>
                                  <TableHead className="text-center">Grade Avg</TableHead>
                                  <TableHead className="text-center">Core Subjects</TableHead>
                                  <TableHead className="text-center">Status</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {paginatedStudents.length === 0 ? (
                                  <TableRow>
                                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground border-t border-[#E2E8F0] dark:border-[#1E293B]">
                                      No students found matching your filters.
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  paginatedStudents.map((student) => {
                                    const eligibility = checkEligibility(student);
                                    const isPromotedThisYear = student.promotionYear === academicYear;
                                    console.log(`Student ${student.name}: promotedThisYear=${isPromotedThisYear}, eligible=${eligibility.eligible}`);
                                    return (
                                      <TableRow key={student.id} className={isPromotedThisYear ? "opacity-50 border-t border-[#E2E8F0] dark:border-[#1E293B]" : "border-t border-[#E2E8F0] dark:border-[#1E293B]"}>
                                        <TableCell>
                                          <Checkbox
                                            checked={selectedStudents.includes(student.id)}
                                            disabled={isPromotedThisYear}
                                            onCheckedChange={(checked) => {
                                              if (checked) {
                                                setSelectedStudents([...selectedStudents, student.id]);
                                              } else {
                                                setSelectedStudents(selectedStudents.filter(id => id !== student.id));
                                              }
                                            }}
                                          />
                                        </TableCell>
                                        <TableCell className="font-mono">{student.registrationNumber}</TableCell>
                                        <TableCell className="font-medium">{student.name}</TableCell>
                                        <TableCell>{classes.find(c => c.classUid === student.currentClassUid)?.name || student.currentClass}</TableCell>
                                        <TableCell className="text-center">
                                          <Badge variant={student.gradeAverage >= rules.minGradeAverage || disableEligibilityRules ? "default" : "destructive"} className="bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B]">
                                            {student.gradeAverage}%
                                          </Badge>
                                        </TableCell>
                                        <TableCell className="text-center">
                                          {student.coreSubjectsPassed || disableEligibilityRules ? (
                                            <CheckCircle2 className="h-5 w-5 text-green-600 mx-auto" />
                                          ) : (
                                            <XCircle className="h-5 w-5 text-red-600 mx-auto" />
                                          )}
                                        </TableCell>
                                        <TableCell className="text-center">
                                          {isPromotedThisYear ? (
                                            <Badge variant="default" className="bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B]">Promoted</Badge>
                                          ) : eligibility.eligible ? (
                                            <Badge variant="default" className="bg-green-600">Eligible</Badge>
                                          ) : (
                                            <Popover>
                                              <PopoverTrigger>
                                                <Badge variant="destructive" className="cursor-pointer bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B]">
                                                  <AlertCircle className="h-3 w-3 mr-1" />
                                                  Flagged
                                                </Badge>
                                              </PopoverTrigger>
                                              <PopoverContent className="w-80 bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B]">
                                                <div className="space-y-2">
                                                  <h4 className="font-medium text-sm">Issues:</h4>
                                                  <ul className="text-xs space-y-1">
                                                    {eligibility.reasons.map((reason, idx) => (
                                                      <li key={idx} className="text-muted-foreground">• {reason}</li>
                                                    ))}
                                                  </ul>
                                                </div>
                                              </PopoverContent>
                                            </Popover>
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })
                                )}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                        {totalPages > 1 && (
                          <div className="mt-4">
                            <Pagination>
                              <PaginationContent className="bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg">
                                <PaginationItem>
                                  <PaginationPrevious
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                                  />
                                </PaginationItem>
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                                  if (
                                    page === 1 ||
                                    page === totalPages ||
                                    (page >= currentPage - 1 && page <= currentPage + 1)
                                  ) {
                                    return (
                                      <PaginationItem key={page}>
                                        <PaginationLink
                                          onClick={() => setCurrentPage(page)}
                                          isActive={currentPage === page}
                                          className="cursor-pointer bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B]"
                                        >
                                          {page}
                                        </PaginationLink>
                                      </PaginationItem>
                                    );
                                  } else if (page === currentPage - 2 || page === currentPage + 2) {
                                    return (
                                      <PaginationItem key={page}>
                                        <PaginationEllipsis />
                                      </PaginationItem>
                                    );
                                  }
                                  return null;
                                })}
                                <PaginationItem>
                                  <PaginationNext
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                                  />
                                </PaginationItem>
                              </PaginationContent>
                            </Pagination>
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              <TabsContent value="rules" className="space-y-4">
                <Card className="m-2 bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B]">
                  <CardHeader>
                    <CardTitle>Eligibility Rules</CardTitle>
                    <CardDescription>Configure conditions for automatic student promotion</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex items-center justify-between border-b border-[#E2E8F0] dark:border-[#1E293B] pb-4">
                      <div className="space-y-0.5">
                        <Label>Disable Eligibility Rules</Label>
                        <p className="text-xs text-muted-foreground">Allow all students to be promoted regardless of rules</p>
                      </div>
                      <Switch
                        checked={disableEligibilityRules}
                        onCheckedChange={setDisableEligibilityRules}
                      />
                    </div>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="minGrade">Minimum Grade Average (%)</Label>
                        <Input
                          id="minGrade"
                          type="number"
                          min="0"
                          max="100"
                          value={rules.minGradeAverage}
                          onChange={(e) => setRules({ ...rules, minGradeAverage: parseInt(e.target.value) || 0 })}
                          disabled={disableEligibilityRules}
                          className="bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B]"
                        />
                        <p className="text-xs text-muted-foreground">Students must have at least this grade average</p>
                      </div>
                      <div className="flex items-center justify-between border-t border-[#E2E8F0] dark:border-[#1E293B] pt-4">
                        <div className="space-y-0.5">
                          <Label>Require Core Subjects Pass</Label>
                          <p className="text-xs text-muted-foreground">Students must pass all core subjects</p>
                        </div>
                        <Switch
                          checked={rules.requireCoreSubjects}
                          onCheckedChange={(checked) => setRules({ ...rules, requireCoreSubjects: checked })}
                          disabled={disableEligibilityRules}
                        />
                      </div>
                      <div className="flex items-center justify-between border-t border-[#E2E8F0] dark:border-[#1E293B] pt-4">
                        <div className="space-y-0.5">
                          <Label>Require Teacher Approval</Label>
                          <p className="text-xs text-muted-foreground">Teachers must approve before promotion</p>
                        </div>
                        <Switch
                          checked={rules.requireTeacherApproval}
                          onCheckedChange={(checked) => setRules({ ...rules, requireTeacherApproval: checked })}
                          disabled={disableEligibilityRules}
                        />
                      </div>
                    </div>
                    <div className="pt-4 border-t border-[#E2E8F0] dark:border-[#1E293B]">
                      <h3 className="font-medium mb-3">Quick Rules Presets</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        <Button
                          variant="outline"
                          onClick={() => setRules({ minGradeAverage: 0, requireCoreSubjects: false, requireTeacherApproval: false })}
                          className="w-full bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B] m-2"
                          disabled={disableEligibilityRules}
                        >
                          Promote All
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setRules({ minGradeAverage: 50, requireCoreSubjects: true, requireTeacherApproval: false })}
                          className="w-full bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B] m-2"
                          disabled={disableEligibilityRules}
                        >
                          Standard Rules
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setRules({ minGradeAverage: 70, requireCoreSubjects: true, requireTeacherApproval: true })}
                          className="w-full sm:col-span-2 lg:col-span-1 bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B] m-2"
                          disabled={disableEligibilityRules}
                        >
                          Strict Rules
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              <TabsContent value="schedule" className="space-y-4">
                <Card className="m-2 bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B]">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                       <Clock className="h-5 w-5 text-indigo-600" />
                       Automatic Promotion Schedule
                    </CardTitle>
                    <CardDescription>
                      Configure the system to automatically promote eligible students at a specific date and time.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex items-center space-x-4 p-4 border border-indigo-100 dark:border-indigo-900/30 bg-indigo-50/50 dark:bg-indigo-900/10 rounded-lg">
                      <div className="flex-1">
                        <Label htmlFor="auto-promote-switch" className="text-base font-semibold">Enable Auto-Promotion</Label>
                        <p className="text-sm text-gray-500">System runs hourly checks even if you are offline.</p>
                      </div>
                      <Switch
                        id="auto-promote-switch"
                        checked={autoPromotionEnabled}
                        onCheckedChange={(checked) => saveScheduleSettings(scheduledDate, checked)}
                        className="data-[state=checked]:bg-indigo-600 data-[state=unchecked]:bg-gray-200 dark:data-[state=unchecked]:bg-gray-700"
                      />
                    </div>
                    
                    {autoPromotionEnabled && (
                      <div className="space-y-6">
                        <div className="space-y-2">
                           <Label htmlFor="academicYearSettings" className="font-medium text-sm">Target Academic Year</Label>
                           <Select
                               value={academicYear}
                               onValueChange={setAcademicYear}
                           >
                               <SelectTrigger id="academicYearSettings" className="bg-white dark:bg-[#0f172a]">
                                   <SelectValue placeholder="Select Year" />
                               </SelectTrigger>
                               <SelectContent>
                                   <SelectItem value="2023-2024">2023-2024</SelectItem>
                                   <SelectItem value="2024-2025">2024-2025</SelectItem>
                                   <SelectItem value="2025-2026">2025-2026</SelectItem>
                                   <SelectItem value="2026-2027">2026-2027</SelectItem>
                               </SelectContent>
                           </Select>
                           <p className="text-xs text-muted-foreground">Select the academic year for this promotion</p>
                        </div>

                        <div className="space-y-4">
                           <div className="space-y-2">
                             <Label className="font-medium text-sm flex items-center gap-2">
                               <CalendarIcon className="h-4 w-4" /> Promotion Date & Time
                             </Label>
                             <div className="flex flex-col sm:flex-row gap-4">
                               <Popover>
                                 <PopoverTrigger asChild>
                                   <Button
                                     variant="outline"
                                     className={
                                       "flex-1 justify-start text-left font-normal bg-white dark:bg-[#0f172a] " +
                                       (!scheduledDate ? " text-muted-foreground" : "")
                                     }
                                   >
                                     <CalendarIcon className="mr-2 h-4 w-4" />
                                     {scheduledDate ? format(scheduledDate, "PPP") : "Pick a date"}
                                   </Button>
                                 </PopoverTrigger>
                                  <PopoverContent className="w-auto p-0 bg-white dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B] shadow-lg rounded-lg" align="start">
                                    <Calendar
                                      mode="single"
                                      selected={scheduledDate}
                                      onSelect={(date) => {
                                        if (date) {
                                            const newDate = new Date(date);
                                            newDate.setHours(2, 0); // Always set to 2 AM
                                            saveScheduleSettings(newDate, autoPromotionEnabled);
                                        }
                                      }}
                                      initialFocus
                                      className="pointer-events-auto bg-white dark:bg-[#020817] rounded-md"
                                    />
                                  </PopoverContent>
                               </Popover>
                             </div>
                             <p className="text-[11px] text-muted-foreground">
                               Typical: End of academic year. The system runs automatically at 2:00 AM on the selected date.
                             </p>
                           </div>
                        </div>

                        {scheduledDate && (
                            <div className={`p-4 rounded-lg border flex items-center gap-3 transition-colors ${
                                autoPromotionEnabled 
                                ? "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800" 
                                : "bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700"
                            }`}>
                                <CalendarIcon className={`h-8 w-8 ${autoPromotionEnabled ? "text-green-600" : "text-gray-400"}`} />
                                <div>
                                    <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Scheduled Run</p>
                                    <p className="text-sm font-bold text-gray-900 dark:text-gray-100">
                                        {format(scheduledDate, "PPP")} at 2:00 AM
                                    </p>
                                    <div className="flex items-center gap-2 mt-1">
                                        <div className={`h-2 w-2 rounded-full ${autoPromotionEnabled ? "bg-green-500 animate-pulse" : "bg-gray-400"}`} />
                                         <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                                            {autoPromotionEnabled ? "System Active (Online/Offline)" : "Disabled"}
                                         </span>
                                    </div>
                                </div>
                            </div>
                        )}

                        <Button onClick={handleSchedulePromotion} className="w-full bg-blue-600 hover:bg-blue-700 font-semibold shadow-sm text-white mt-2">
                          <Save className="mr-2 h-4 w-4" />
                          Save Configuration
                        </Button>

                        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                          <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Test Auto-Promotion</Label>
                          <p className="text-xs text-muted-foreground mt-1 mb-3">Run the exact same promotion logic now (without waiting for schedule)</p>
                          <Button 
                            variant="outline"
                            className="w-full border-green-500 text-green-600 hover:bg-green-50 dark:border-green-700 dark:text-green-400 dark:hover:bg-green-900/20 font-semibold"
                            onClick={async () => {
                              try {
                                const functions = getFunctions();
                                const testPromotion = httpsCallable(functions, 'testPromotion');
                                toast.info('Running test promotion...', { duration: 3000 });
                                const result = await testPromotion({ academicYear }) as { data: { success: boolean; error?: string; promotedCount: number; graduatedCount: number } };
                                if (result.data.success) {
                                  toast.success(`Test Complete: Promoted ${result.data.promotedCount} students, Graduated ${result.data.graduatedCount} to Alumni`, { duration: 8000 });
                                } else {
                                  toast.error(`Failed: ${result.data.error}`, { duration: 5000 });
                                }
                              } catch (error: any) {
                                console.error('Test promotion error:', error);
                                toast.error(error.message || 'Test promotion failed');
                              }
                            }}
                          >
                            <TrendingUp className="mr-2 h-4 w-4" />
                            Test Auto-Promotion Now
                          </Button>
                          
                          <Button 
                            variant="outline"
                            className="w-full mt-2 border-orange-500 text-orange-600 hover:bg-orange-50 dark:border-orange-700 dark:text-orange-400 dark:hover:bg-orange-900/20 font-semibold"
                            onClick={async () => {
                              if (!confirm("This will scan all students and fix any mismatches between their class name and internal ID. This is safe to run. Continue?")) return;
                              try {
                                const functions = getFunctions();
                                // Note: Ensure 'fixStudentData' is deployed!
                                const fixStudentData = httpsCallable(functions, 'fixStudentData');
                                toast.info('Repairing student data...', { duration: 3000 });
                                const result = await fixStudentData({ schoolID }) as { data: { success: boolean; error?: string; message?: string; mismatches?: number } };
                                if (result.data.success) {
                                  toast.success(`Repair Complete: ${result.data.message}`, { duration: 8000 });
                                  // Refresh students
                                  if (result.data.mismatches && result.data.mismatches > 0) {
                                     toast.info("Refetching student data...", { duration: 2000 });
                                     // Trigger a reload if possible or just wait for next interaction
                                  }
                                } else {
                                  toast.error(`Repair Failed: ${result.data.error}`, { duration: 5000 });
                                }
                              } catch (error: any) {
                                console.error('Repair error:', error);
                                toast.error(error.message || 'Repair failed');
                              }
                            }}
                          >
                            <Wrench className="mr-2 h-4 w-4" />
                            Repair Data (Fix Inconsistencies)
                          </Button>
                          
                          <Button 
                            variant="outline"
                            className="w-full mt-2 border-green-500 text-green-600 hover:bg-green-50 dark:border-green-700 dark:text-green-400 dark:hover:bg-green-900/20 font-semibold"
                            onClick={async () => {
                              if (!confirm("This will immediately approve ALL pending promotions, preventing them from being reverted. This simulates the 30-day auto-approval. Continue?")) return;
                              await handleAutoApproveAll();
                            }}
                          >
                            <CheckCircle2 className="mr-2 h-4 w-4" />
                            Test Auto-Approval (Approve All Now)
                          </Button>
                        </div>

                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              <TabsContent value="revert" className="space-y-4">
                <Card className="m-2 bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B]">
                  <CardHeader>
                    <CardTitle>Revert Promotions</CardTitle>
                    <CardDescription>Undo student promotions and move them back to previous level or approve promotions to finalize them</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {promotedStudents.length === 0 ? (
                      <div className="text-center py-12">
                        <Undo2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-foreground mb-2">No Promoted Students</h3>
                        <p className="text-muted-foreground">
                          There are no students who have been promoted yet in {academicYear}.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4 max-w-full">
                        <div className="border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg overflow-hidden max-w-full">
                          <div className="overflow-x-auto max-w-full">
                            <Table className="min-w-full bg-[#FFFFFF] dark:bg-[#020817]">
                              <TableHeader>
                                <TableRow className="border-b border-[#E2E8F0] dark:border-[#1E293B]">
                                  <TableHead className="w-12">
                                    <Checkbox
                                      checked={selectedForApproval.length === promotedStudents.length}
                                      onCheckedChange={(checked) => {
                                        if (checked) {
                                          setSelectedForApproval(promotedStudents.map(s => s.id));
                                        } else {
                                          setSelectedForApproval([]);
                                        }
                                      }}
                                    />
                                  </TableHead>
                                  <TableHead className="w-32">Reg No.</TableHead>
                                  <TableHead>Student Name</TableHead>
                                  <TableHead>Current Class</TableHead>
                                  <TableHead>Previous Class</TableHead>
                                  <TableHead>Grade Avg</TableHead>
                                  <TableHead className="text-center">Core Subjects</TableHead>
                                  <TableHead className="text-right">Action</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {promotedStudents.slice(0, 15).map((student) => {
                                  const historyEntry = promotionHistory.find(h => h.studentId === student.id && h.promotionYear === academicYear);
                                  return (
                                    <TableRow key={student.id} className="border-t border-[#E2E8F0] dark:border-[#1E293B]">
                                      <TableCell>
                                        <Checkbox
                                          checked={selectedForApproval.includes(student.id)}
                                          onCheckedChange={(checked) => {
                                            if (checked) {
                                              setSelectedForApproval([...selectedForApproval, student.id]);
                                            } else {
                                              setSelectedForApproval(selectedForApproval.filter(id => id !== student.id));
                                            }
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell className="font-mono">{student.registrationNumber}</TableCell>
                                      <TableCell className="font-medium">{student.name}</TableCell>
                                      <TableCell>{classes.find(c => c.classUid === student.currentClassUid)?.name || student.currentClass}</TableCell>
                                      <TableCell>{historyEntry?.fromClass || "N/A"}</TableCell>
                                      <TableCell>{student.gradeAverage}%</TableCell>
                                      <TableCell className="text-center">
                                        {student.coreSubjectsPassed || disableEligibilityRules ? (
                                          <CheckCircle2 className="h-5 w-5 text-green-600 mx-auto" />
                                        ) : (
                                          <XCircle className="h-5 w-5 text-red-600 mx-auto" />
                                        )}
                                      </TableCell>
                                      <TableCell className="text-right">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => handleRevertPromotion([student.id])}
                                          className="flex items-center gap-2 bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B] m-2"
                                        >
                                          <Undo2 className="h-3 w-3" />
                                          Revert
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2">
                          <Button
                            variant="destructive"
                            onClick={() => handleRevertPromotion(promotedStudents.map(s => s.id))}
                            className="flex items-center gap-2 m-2"
                          >
                            <Undo2 className="h-4 w-4" />
                            Revert All Promotions ({promotedStudents.length})
                          </Button>
                          <Button
                            onClick={() => handleApprovePromotions(selectedForApproval)}
                            disabled={selectedForApproval.length === 0}
                            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white m-2"
                          >
                            <CheckCircle2 className="h-4 w-4" />
                            Approve Selected Promotions ({selectedForApproval.length})
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* ============ PROMOTION SIMULATOR TAB ============ */}
              <TabsContent value="simulator" className="space-y-4">
                <Card className="m-2 bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B]">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-purple-600" />
                      Promotion Simulator
                    </CardTitle>
                    <CardDescription>
                      Test the promotion hierarchy by stepping through classes one at a time. Select a student and use Next/Back to simulate promotions.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Student Selector */}
                    <div className="space-y-2">
                      <Label htmlFor="simulator-student">Select Student</Label>
                      <Select
                        value={simulatorStudentId}
                        onValueChange={(value) => {
                          setSimulatorStudentId(value);
                          setSimulatorHistory([]); // Reset navigation history
                        }}
                      >
                        <SelectTrigger id="simulator-student" className="w-full bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B]">
                          <SelectValue placeholder="Choose a student to test..." />
                        </SelectTrigger>
                        <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B] max-h-[300px]">
                          {students.map((student) => (
                            <SelectItem key={student.id} value={student.id}>
                              {student.name} ({student.registrationNumber}) - {classes.find(c => c.classUid === student.currentClassUid)?.name || student.currentClass}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Current Status Display */}
                    {simulatorStudentId && (
                      <div className="p-6 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border border-purple-200 dark:border-purple-800 rounded-xl">
                        <div className="text-center space-y-4">
                          <div>
                            <p className="text-sm text-purple-600 dark:text-purple-400 font-medium">Selected Student</p>
                            <p className="text-xl font-bold text-gray-900 dark:text-white">
                              {getSelectedSimulatorStudent()?.name || 'Unknown'}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {getSelectedSimulatorStudent()?.registrationNumber}
                            </p>
                          </div>
                          
                          <div className="flex flex-col items-center">
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Current Class</span>
                            <div className="px-5 py-2 bg-purple-50 dark:bg-purple-900/20 rounded-full border border-purple-100 dark:border-purple-800/50 flex flex-col items-center shadow-sm">
                              <p className="text-xl font-bold text-purple-700 dark:text-purple-300">
                                {getSimulatorCurrentClass()?.name || 'Not Set'}
                              </p>
                              <p className="text-[10px] uppercase font-medium text-purple-500/80 mt-0.5">
                                Level {getSimulatorCurrentClass()?.level || '?'} • Stream {getSimulatorCurrentClass()?.stream || '?'}
                              </p>
                            </div>
                          </div>
                          
                          {/* Navigation History */}
                          {simulatorHistory.length > 0 && (
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              <p className="font-medium">Navigation History ({simulatorHistory.length} steps):</p>
                              <p className="text-xs">
                                {simulatorHistory.map(uid => classes.find(c => c.classUid === uid)?.name || uid).join(' → ')} → <span className="font-bold">{getSimulatorCurrentClass()?.name}</span>
                              </p>
                            </div>
                          )}

                          {/* Next Class Preview */}
                          {/* Next Class Preview */}
                          <div className="flex items-center justify-center mt-2">
                            <span className="text-sm text-gray-500 dark:text-gray-400 mr-2">Target Class:</span>
                            <Badge variant="outline" className="text-sm py-1 px-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800 font-medium">
                                {(() => {
                                  const current = getSimulatorCurrentClass();
                                  if (!current) return 'Select a student';
                                  
                                  // Check if nextClassUid is set (direct pointer)
                                  if (current.nextClassUid === 'ALUMNI') return 'Alumni (Graduation)';
                                  if (current.nextClassUid) {
                                    const next = classes.find(c => c.classUid === current.nextClassUid);
                                    return next?.name || 'Not Found';
                                  }
                                  
                                  // Fallback calculation based on stage type
                                  const currentStage = current.stage || current.stageType;
                                  const currentStream = (current.stream || '').toLowerCase().trim();
                                  const currentNaming = (current.namingConvention || '').toLowerCase().trim();
                                  
                                  if (currentStage === 'final') {
                                    return 'Alumni (Graduation)';
                                  } else if (currentStage === 'progressive') {
                                    // PROGRESSIVE: Find Level 1 class with SAME stream but DIFFERENT naming
                                    const bridgeClass = classes.find(c => {
                                      const cStream = (c.stream || '').toLowerCase().trim();
                                      const cNaming = (c.namingConvention || '').toLowerCase().trim();
                                      return c.level === 1 && cStream === currentStream && cNaming !== currentNaming;
                                    });
                                    return bridgeClass?.name || 'No Bridge Found';
                                  } else {
                                    // STANDARD: Same naming, level + 1
                                    const nextClass = classes.find(c => 
                                      c.level === current.level + 1 &&
                                      (c.stream || '').toLowerCase().trim() === currentStream &&
                                      (c.namingConvention || '').toLowerCase().trim() === currentNaming
                                    );
                                    return nextClass?.name || 'End of Chain';
                                  }
                                })()}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Control Buttons */}
                    <div className="flex justify-center gap-4">
                      <Button
                        variant="outline"
                        size="lg"
                        onClick={simulateBack}
                        disabled={!simulatorStudentId || isSimulating || simulatorHistory.length === 0}
                        className="flex items-center gap-2 px-8 border-2 border-gray-300 dark:border-gray-600"
                      >
                        <ArrowLeft className="h-5 w-5" />
                        Back
                      </Button>
                      <Button
                        size="lg"
                        onClick={simulateNext}
                        disabled={!simulatorStudentId || isSimulating}
                        className="flex items-center gap-2 px-8 bg-purple-600 hover:bg-purple-700 text-white"
                      >
                        Next
                        <TrendingUp className="h-5 w-5" />
                      </Button>
                    </div>

                    {isSimulating && (
                      <div className="text-center">
                        <p className="text-sm text-gray-500 animate-pulse">Processing...</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
            <Dialog open={promoteDialogOpen} onOpenChange={setPromoteDialogOpen}>
              <DialogContent className="bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B]">
                <DialogHeader>
                  <DialogTitle className="text-gray-900 dark:text-white">Confirm Promotion</DialogTitle>
                  <DialogDescription className="text-gray-600 dark:text-gray-400">
                    {pendingPromoteType === 'all'
                      ? `Are you sure you want to promote ${eligibleStudents.length} student(s)? This action will move them to the next class level for the ${academicYear} academic year. For example, a student in Form 1 Blue will be moved to Form 2 Blue if it exists. Students without a matching next class will be skipped.`
                      : `Are you sure you want to promote ${selectedStudents.length} selected student(s)? This action will move them to the next class level for the ${academicYear} academic year. For example, a student in Form 1 Blue will be moved to Form 2 Blue if it exists. Students without a matching next class will be skipped.`
                    }
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setPromoteDialogOpen(false)} className="m-2">
                    Cancel
                  </Button>
                  <Button onClick={confirmPromote} className="bg-blue-600 hover:bg-blue-700 text-white m-2">
                    Confirm Promotion
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={revertDialogOpen} onOpenChange={setRevertDialogOpen}>
              <DialogContent className="bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B]">
                <DialogHeader>
                  <DialogTitle className="text-gray-900 dark:text-white">Confirm Revert Promotion</DialogTitle>
                  <DialogDescription className="text-gray-600 dark:text-gray-400">
                    Are you sure you want to revert promotion for {revertIds.length} student(s)? This will move them back to their previous class.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setRevertDialogOpen(false)} className="m-2">
                    Cancel
                  </Button>
                  <Button onClick={confirmRevert} variant="destructive" className="m-2">
                    Confirm Revert
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
              <DialogContent className="bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B]">
                <DialogHeader>
                  <DialogTitle className="text-gray-900 dark:text-white">Confirm Approve Promotions</DialogTitle>
                  <DialogDescription className="text-gray-600 dark:text-gray-400">
                    Are you sure you want to approve promotions for {selectedForApproval.length} student(s)? This will finalize their current class (e.g., 2 Red) and make them eligible for future promotions. Promotion history will be cleared.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setApproveDialogOpen(false)} className="m-2">
                    Cancel
                  </Button>
                  <Button onClick={confirmApprove} className="bg-green-600 hover:bg-green-700 text-white m-2">
                    Confirm Approve
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={scheduleConfirmOpen} onOpenChange={setScheduleConfirmOpen}>
                <DialogContent className="bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-indigo-600">
                             <Clock className="h-5 w-5" />
                             Confirm Automatic Schedule
                        </DialogTitle>
                        <DialogDescription className="space-y-3 pt-2">
                            <div className="p-3 bg-indigo-50 dark:bg-indigo-900/10 rounded-lg border border-indigo-100 dark:border-indigo-900/30">
                                <p className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                                    You are about to schedule an automatic promotion:
                                </p>
                                <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 dark:text-gray-300">
                                    <li>Date: <span className="font-medium text-gray-900 dark:text-white">{pendingScheduleSettings?.date ? format(pendingScheduleSettings.date, "PPP") : ""}</span></li>
                                    <li>Time: <span className="font-medium text-gray-900 dark:text-white">{pendingScheduleSettings?.date ? format(pendingScheduleSettings.date, "p") : ""}</span></li>
                                    <li>Status: <span className={pendingScheduleSettings?.enabled ? "text-green-600 font-bold" : "text-gray-500"}>{pendingScheduleSettings?.enabled ? "Active" : "Disabled"}</span></li>
                                </ul>
                            </div>
                            <p className="text-sm text-gray-500">
                                {pendingScheduleSettings?.enabled 
                                ? "The system will automatically promote all eligible students at this time. This action runs on the cloud server and does not require you to be online."
                                : "Saving this will update the schedule but keep the automation DISABLED."}
                            </p>
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setScheduleConfirmOpen(false)} className="m-2">Cancel</Button>
                        <Button onClick={confirmScheduleSettings} className="bg-indigo-600 hover:bg-indigo-700 text-white m-2">
                            {pendingScheduleSettings?.enabled ? "Confirm & Schedule" : "Save Changes"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
              </main>
            </div>
          </div>
        </SidebarProvider>
      </div>
    </>
  );
};

export default PromoteStudentsForm;